import React, { memo, useState, useEffect, useRef, useMemo } from 'react';
import { Box, Typography } from '@mui/material';

import {
  DailyVideo,
  useAppMessage,
  useMediaTrack,
} from '@daily-co/daily-react';

import { useSessionContext } from '../../contexts/SessionContext';

import VideoPlaceholder from '../../components/VideoPlaceholder';
import { useInspectorStyles } from './styles';

import Pointer from '../../icons/Pointer';

import { styled } from '@mui/system';
import NetworkStats from '../../components/NetworkStats';
import { useBookingsContext } from '../../contexts/BookingsContext';
import { DailyParticipant } from '@daily-co/daily-js';

const PointerWrapper = styled('div')({
  position: 'absolute',
  pointerEvents: 'none',
  zIndex: 1000,
});

const getBounds = (bounds: DOMRect) => {
  return {
    left: bounds.left,
    top: bounds.top,
    right: bounds.right,
    bottom: bounds.bottom,
    width: bounds.width,
    height: bounds.height,
  };
};

const NetworkStatsComponent = () => {
  const { userSettings } = useBookingsContext();

  const [remoteNetworkStats, setRemoteNetworkStats] = useState<any>({
    quality: 0,
    recv: 0,
    send: 0,
  });

  useAppMessage({
    onAppMessage: ({ data, ...rest }: any) => {
      if (data?.type !== 'network-stats') return;

      if (userSettings?.stats)
        console.log('::::remoteNetworkStats', data.stats);
      setRemoteNetworkStats(data.stats);
    },
  });

  return (
    <NetworkStats
      sx={{ width: '7rem' }}
      quality={remoteNetworkStats.quality}
      recv={remoteNetworkStats.recv}
      send={remoteNetworkStats.send}
    />
  );
};

interface VideoComponentProps {
  sessionId: string;
  remoteVideoRef: React.RefObject<HTMLVideoElement>;
}

const VideoComponent: React.FC<VideoComponentProps> = React.memo(
  ({ sessionId, remoteVideoRef }) => {
    const [videoSize, setVideoSize] = useState<string>('');
    const [clientSize, setClientSize] = useState<string>('');

    const latestLagRef = useRef<number>(0);
    const frameSkipThreshold = 500;

    useEffect(() => {
      let canceled = false;

      const handleFrame = (
        now: DOMHighResTimeStamp,
        metadata: VideoFrameCallbackMetadata,
      ) => {
        const lag = performance.now() - metadata.expectedDisplayTime;
        latestLagRef.current = lag;

        if (
          metadata.expectedDisplayTime - performance.now() >
          frameSkipThreshold
        ) {
          console.warn(
            `Skipping delayed frame (too early by ${(metadata.expectedDisplayTime - performance.now()).toFixed(2)} ms)`,
          );
          return;
        }

        if (!canceled && remoteVideoRef.current) {
          remoteVideoRef.current.requestVideoFrameCallback(handleFrame);
        }
      };

      if (remoteVideoRef.current) {
        remoteVideoRef.current.requestVideoFrameCallback(handleFrame);
      }

      const intervalId = setInterval(() => {
        if (remoteVideoRef.current) {
          setVideoSize(
            `Stream: ${remoteVideoRef.current?.videoWidth} x ${remoteVideoRef.current?.videoHeight}`,
          );
          setClientSize(
            `Display: ${remoteVideoRef.current?.clientWidth} x ${remoteVideoRef.current?.clientHeight}`,
          );
        }
      }, 1000);

      return () => {
        canceled = true;
        clearInterval(intervalId);
      };
    }, [sessionId]);

    console.log(
      '::::rerender DailyVideo for remoteParticipant sessionId: ',
      sessionId,
      'remoteVideoRef: ',
      remoteVideoRef,
    );

    return (
      <>
        <DailyVideo
          ref={remoteVideoRef}
          sessionId={sessionId}
          type="video"
          automirror={false}
          style={{
            height: '100%',
            width: '100%',
            objectFit: 'cover',
            borderRadius: '.625rem',
          }}
        />
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            padding: '0.5rem',
            backgroundColor: 'transparent',
          }}>
          <Typography color="white">
            Remote Participant
            <br />
            {videoSize}
            <br />
            {clientSize}
          </Typography>
        </Box>
      </>
    );
  },
);

interface IRemoteParticipantProps {
  sendPayload: (props: any) => void;
  participant: DailyParticipant | undefined;
  remoteVideoRef: any;
  remoteVideoSize: any;
}

const RemoteParticipant = ({
  sendPayload,
  participant,
  remoteVideoRef,
  remoteVideoSize,
}: IRemoteParticipantProps) => {
  const styles = useInspectorStyles();

  const { canvasSketchSize, setCanvasSketchSize } = useSessionContext();

  const video = useMediaTrack(participant?.session_id || '', 'video');

  const componentRef = useRef<any>();
  const [componentBounds, setComponentBounds] = useState<any>();
  const [canvasArea, setCanvasArea] = useState<any>();

  useEffect(() => {
    const component = componentRef?.current;
    if (
      !componentBounds?.width ||
      !componentBounds?.height ||
      !remoteVideoSize ||
      !component
    )
      return;

    const sx = componentBounds.width / remoteVideoSize.width;
    const sy = componentBounds.height / remoteVideoSize.height;
    const s = sx > sy ? sx : sy;

    const heightRatio =
      sx > sy ? 1 : (sx * remoteVideoSize.height) / componentBounds.height;
    const widthRatio =
      sx > sy ? (sy * remoteVideoSize.width) / componentBounds.width : 1;

    component.style.height = `${Math.round(100 * heightRatio)}%`;
    component.style.width = `${Math.round(100 * widthRatio)}%`;

    const _canvasSketchSize = {
      height: Math.round(componentBounds.height * heightRatio),
      width: Math.round(componentBounds.width * widthRatio),
    };

    if (
      _canvasSketchSize.height === canvasSketchSize.height &&
      _canvasSketchSize.height === canvasSketchSize.height
    )
      return;

    console.log(
      `bounds width: ${componentBounds.width} height: ${componentBounds.height}`,
      `remoteVideoSize width: ${remoteVideoSize.width} height: ${remoteVideoSize.height}`,
      `sx: ${sx}, sy ${sy}`,
    );
    console.log(
      `component: ${component.style.width} x ${component.style.height}`,
    );
    console.log(':::setCanvasSketchSize', _canvasSketchSize);
    console.log(':::componentBounds', componentBounds);

    setCanvasSketchSize(_canvasSketchSize);

    setCanvasArea({
      left:
        componentBounds.left +
        (componentBounds.width - _canvasSketchSize.width) / 2,
      top:
        componentBounds.top +
        (componentBounds.height - _canvasSketchSize.height) / 2,
      right:
        componentBounds.left +
        (componentBounds.width + _canvasSketchSize.width) / 2,
      bottom:
        componentBounds.top +
        (componentBounds.height + _canvasSketchSize.height) / 2,
      width: _canvasSketchSize.width,
      height: _canvasSketchSize.height,
    });
  }, [componentBounds, remoteVideoSize, componentRef]);

  useEffect(() => {
    const resizeObserver = new ResizeObserver((entries) => {
      requestAnimationFrame(() => {
        if (!entries[0]) return;
        setComponentBounds(
          getBounds(entries[0].target.getBoundingClientRect()),
        );
      });
    });

    if (componentRef.current) {
      resizeObserver.observe(componentRef.current.parentElement);
      setComponentBounds(
        getBounds(componentRef.current.getBoundingClientRect()),
      );
    }

    return () => {
      if (componentRef.current) {
        resizeObserver.unobserve(componentRef.current.parentElement);
      }
    };
  }, [componentRef.current]);

  const [position, setPosition] = useState({ x: 0, y: 0 });
  const [lastSentTime, setLastSentTime] = useState<number>(0);

  const handleMouseMove = (ev: any) => {
    if (!componentRef.current || !canvasArea) return;

    setPosition({
      x: ev.clientX - componentBounds.left,
      y: ev.clientY - componentBounds.top,
    });

    const remotePosition = {
      x:
        ev.clientX - canvasArea.left <= 0 ||
        ev.clientX - canvasArea.left >= canvasArea.width
          ? 0
          : Math.round(
              (remoteVideoSize.width * (ev.clientX - canvasArea.left)) /
                canvasArea.width,
            ),
      y:
        ev.clientY - canvasArea.top <= 0 ||
        ev.clientY - canvasArea.top >= canvasArea.height
          ? 0
          : Math.round(
              (remoteVideoSize.height * (ev.clientY - canvasArea.top)) /
                canvasArea.height,
            ),
    };

    const currentTime = Date.now();
    if (
      remotePosition.x > 0 &&
      remotePosition.y > 0 &&
      currentTime - lastSentTime < 100
    )
      return;
    setLastSentTime(currentTime);

    sendPayload({
      type: 'mouse-move',
      mousePosition: remotePosition,
    });
  };

  const handleMouseOut = () => {
    setPosition({ x: 0, y: 0 });

    sendPayload({
      type: 'mouse-move',
      mousePosition: { x: 0, y: 0 },
    });
  };

  return (
    <Box
      id="video-container"
      ref={componentRef}
      onMouseMove={(ev: any) => handleMouseMove(ev)}
      onMouseOut={(ev: any) => handleMouseOut()}
      sx={{
        ...styles.videoContainer,
        display: participant ? 'flex' : 'none',
      }}>
      <NetworkStatsComponent />

      <Box
        sx={{
          transition: 'opacity 0.2s ease-in-out',
          opacity: video?.isOff ? 0 : 1,
          width: '100%',
          height: '100%',
        }}>
        <VideoComponent
          sessionId={participant?.session_id || ''}
          remoteVideoRef={remoteVideoRef}
        />
        <PointerWrapper
          id="pointer-wrapper"
          style={{
            ...styles.pointer,
            left: position.x,
            top: position.y,
            display: !position.x || !position.y ? 'none' : 'block',
          }}>
          <Pointer />
        </PointerWrapper>
      </Box>

      <VideoPlaceholder
        outerLayerSize={250}
        innerLayerSize={150}
        isVisible={video?.isOff}
        participant={participant}
      />
    </Box>
  );
};

export default memo(RemoteParticipant);
